import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Car, Eye, Phone, Mail, Crown, MapPin, Star } from 'lucide-react';
import API from '../services/api';

const Vitrine = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/auth/vitrine/${id}`)
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Chargement...</div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Vendeur non trouvé</div>
    </div>
  );

  const { vendeur, annonces, stats, plan } = data;

  const planLabel = {
    starter: { label: 'Starter Vérifié', color: '#3b82f6' },
    business: { label: 'Business Vérifié', color: '#8b5cf6' },
    premium: { label: 'Premium Vérifié', color: '#F5A623' }
  };

  const planInfo = planLabel[plan];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Bannière vitrine */}
      <div
        style={{
          background: vendeur.banniere
            ? `url(${getImageUrl(vendeur.banniere)}) center/cover`
            : '#0A0A0A',
          position: 'relative',
          minHeight: '280px'
        }}
        className="px-4 relative overflow-hidden"
      >
        {/* Overlay sombre si bannière */}
        <div
          className="absolute inset-0"
          style={{
            background: vendeur.banniere
              ? 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.85))'
              : 'transparent'
          }}
        />

        {/* Ligne décorative dorée */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '2px',
          background: 'linear-gradient(90deg, transparent, #F5A623, transparent)'
        }} />

        {/* Contenu */}
        <div className="relative z-10 max-w-6xl mx-auto py-12 flex flex-col md:flex-row items-center gap-6">

          {/* Avatar */}
          <div
            style={{ border: '3px solid #F5A62380' }}
            className="w-24 h-24 rounded-full flex-shrink-0 overflow-hidden"
          >
            {vendeur.photo_profil ? (
              <img
                src={getImageUrl(vendeur.photo_profil)}
                alt={vendeur.prenom}
                className="w-full h-full object-cover"
              />
            ) : (
              <div style={{ background: '#F5A623' }} className="w-full h-full flex items-center justify-center">
                <span className="text-black font-black text-3xl">
                  {vendeur.prenom?.[0]}{vendeur.nom?.[0]}
                </span>
              </div>
            )}
          </div>

          {/* Infos vendeur */}
          <div className="flex-1 text-center md:text-left">

            {/* Nom + badge plan */}
            <div className="flex items-center gap-3 justify-center md:justify-start mb-2 flex-wrap">
              <h1 className="text-white text-3xl font-black">
                {vendeur.prenom} {vendeur.nom}
              </h1>
              {planInfo && (
                <span
                  style={{ background: planInfo.color + '20', color: planInfo.color, border: `1px solid ${planInfo.color}40` }}
                  className="px-3 py-1 rounded-full text-xs font-black flex items-center gap-1"
                >
                  <Crown size={12} /> {planInfo.label}
                </span>
              )}
            </div>

            {/* Email et téléphone */}
            <div className="flex flex-wrap gap-4 text-gray-300 text-sm justify-center md:justify-start">
              <span className="flex items-center gap-1">
                <Mail size={14} style={{ color: '#F5A623' }} /> {vendeur.email}
              </span>
              {vendeur.telephone && (
                <span className="flex items-center gap-1">
                  <Phone size={14} style={{ color: '#F5A623' }} /> {vendeur.telephone}
                </span>
              )}
            </div>

            {/* Description */}
            {vendeur.description_pro && (
              <p className="text-gray-300 text-sm mt-3 max-w-lg">{vendeur.description_pro}</p>
            )}

            {/* Adresse et horaires */}
            <div className="flex flex-wrap gap-4 mt-3 text-gray-400 text-sm justify-center md:justify-start">
              {vendeur.adresse && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} style={{ color: '#F5A623' }} /> {vendeur.adresse}
                </span>
              )}
              {vendeur.horaires && (
                <span className="flex items-center gap-1">
                  🕐 {vendeur.horaires}
                </span>
              )}
            </div>

            {/* Réseaux sociaux */}
            <div className="flex gap-3 mt-3 justify-center md:justify-start flex-wrap">
              {vendeur.facebook && (
                <a href={vendeur.facebook} target="_blank" rel="noopener noreferrer"
                  style={{ background: '#1877f220', color: '#60a5fa' }}
                  className="px-3 py-1 rounded-full text-xs font-bold hover:opacity-80 transition">
                  📘 Facebook
                </a>
              )}
              {vendeur.instagram && (
                <a href={vendeur.instagram} target="_blank" rel="noopener noreferrer"
                  style={{ background: '#e11d4820', color: '#f87171' }}
                  className="px-3 py-1 rounded-full text-xs font-bold hover:opacity-80 transition">
                  📷 Instagram
                </a>
              )}
              {vendeur.site_web && (
                <a href={vendeur.site_web} target="_blank" rel="noopener noreferrer"
                  style={{ background: '#F5A62320', color: '#F5A623' }}
                  className="px-3 py-1 rounded-full text-xs font-bold hover:opacity-80 transition">
                  🌐 Site web
                </a>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 flex-shrink-0">
            {[
              { label: 'Annonces', value: stats.total_annonces },
              { label: 'Vues', value: stats.total_vues || 0 },
              { label: 'Contacts', value: stats.total_contacts || 0 }
            ].map(({ label, value }) => (
              <div key={label}
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                className="rounded-xl p-4 text-center">
                <p style={{ color: '#F5A623' }} className="text-2xl font-black">{value}</p>
                <p className="text-gray-300 text-xs mt-1">{label}</p>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Annonces */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-gray-900 text-xl font-black">
              Annonces disponibles ({annonces.length})
            </h2>
            <div style={{ width: '40px', height: '3px', background: '#F5A623' }} className="mt-2 rounded" />
          </div>
        </div>

        {annonces.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Car size={60} className="mx-auto mb-4 text-gray-200" />
            <p className="text-gray-400 font-semibold">Aucune annonce disponible</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {annonces.map(annonce => (
              <Link
                key={annonce.id}
                to={`/annonces/${annonce.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group border border-gray-100"
                style={{ borderColor: annonce.est_mise_en_avant ? '#F5A623' : '' }}
              >
                {annonce.est_mise_en_avant && (
                  <div style={{ background: '#F5A623' }} className="text-black text-xs font-black text-center py-1 flex items-center justify-center gap-1">
                    <Star size={10} /> Mise en avant
                  </div>
                )}
                <div className="h-36 bg-gray-100 overflow-hidden relative">
                  {annonce.photo_principale ? (
                    <img
                      src={getImageUrl(annonce.photo_principale)}
                      alt={annonce.titre}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car size={40} className="text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-gray-900 font-semibold text-sm truncate">{annonce.titre}</h3>
                  <p className="font-black text-gray-900 text-base mt-1">
                    {parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA
                  </p>
                  <div className="flex gap-1 text-xs text-gray-500 mt-1 flex-wrap">
                    <span>{annonce.annee}</span>
                    <span>•</span>
                    <span>{annonce.kilometrage.toLocaleString()} km</span>
                    <span>•</span>
                    <span className="capitalize">{annonce.carburant}</span>
                  </div>
                  {annonce.localisation && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 truncate">
                      <MapPin size={10} /> {annonce.localisation}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                    <Eye size={10} /> {annonce.vues} vues
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Vitrine;